import { useCallback, useEffect, useMemo, useState } from "react";
import { Breadcrumb, Form, message } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { ApolloError } from "@apollo/client";

import NewGuideDescriptionCard from "components/new-guide-description-card/new-guide-description-card";
import NewGuideAddVideo from "components/new-guide-add-video/new-guide-add-video";
import { useNewGuideFormStore, useSyncTabsEffect } from "state/zustand-stores/new-guide-form-store";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";

import { useAddGuideMutation } from "graphql/mutations/add-guide.generated";
import { GuideStatus } from "constants/field-values";
import { sanitizeFileName, uploadToStorage } from "utils/files-management";

import { Guides_Insert_Input } from "graphql/generated/graphql";

import { SelectedProduct } from "routes/guide-details/guide-details";
import routeNames from "constants/route-names";

const steps = [
	{
		title: "Guide Description",
		component: "description"
	},
	{
		title: "Add Video",
		component: "video"
	}
];

const GuideAdd = () => {
	const [isUploadingVideo, setIsUploadingVideo] = useState(false);
	const [videoUploadProgress, setVideoUploadProgress] = useState(0);
	const { loggedUserValue } = useLoggedUser();
	const navigate = useNavigate();
	// When navigated from HandoverDetails, handoverId is passed as a query param
	// so the newly created guide can be linked back to the handover.
	const [searchParams] = useSearchParams();
	const handoverId = searchParams.get("handoverId") ?? undefined;
	const [form] = Form.useForm();

	// Enable cross-tab synchronization for form state
	useSyncTabsEffect();

	// Use Zustand store for form state management
	const { formData, setDescription, setSelectedDevices, setVideo, nextStep, clearFormData } = useNewGuideFormStore();

	const { description, selectedDevices, video, currentStep } = formData;

	// clear form data when user leaves page
	useEffect(() => {
		return () => {
			clearFormData();
		};
	}, [clearFormData]);

	const handleOnCompleteAddGuideMutation = useCallback((data: any) => {
		console.log("Complete Add Guide data: ", data);
		navigate(routeNames.GUIDE_DETAILS_EDIT.replace(":guideId", data.insert_guides_one.id));
	}, []);

	const handleOnErrorAddGuideMutation = useCallback((error: ApolloError) => {
		console.log("Error Add Guide Error: ", error);
	}, []);

	const [addGuideMutation, { loading: isUploadingGuide }] = useAddGuideMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorAddGuideMutation,
		onCompleted: handleOnCompleteAddGuideMutation,
		refetchQueries: ["getGuides"]
	});

	// Handlers for form data updates
	const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setDescription(e.target.value);
	}, []);

	const handleDeviceSelect = useCallback((values: SelectedProduct[]) => {
		setSelectedDevices(values);
	}, []);

	const handleVideoRecord = useCallback(() => {
		// Handle video recording logic
		console.log("Start recording video");
	}, []);

	const uploadProductVideo = useCallback(async () => {
		if (formData.video) {
			try {
				const uniqueID = uuidv4();

				const folder = "training-guides-raw-videos/";

				const formattedFileName = sanitizeFileName(formData.video.name);

				const fileName = `raw-video-${Math.floor(Date.now() / 1000)}-${uniqueID}-${formattedFileName}`;

				const { publicUrl: downloadURL } = await uploadToStorage(
					formData.video,
					progress => {
						setVideoUploadProgress(progress);
					},
					`${folder}/${fileName}`
				);

				console.log("Uploaded video URL:", downloadURL);
				message.success(`${formData.video.name} uploaded successfully`);
				return downloadURL;
			} catch (error) {
				console.error("Error uploading file:", error);
				message.error("Failed to upload file.");
				return "";
			}
		}

		return "";
	}, [formData.video, name]);

	const onNext = useCallback(() => {
		if (currentStep < steps.length - 1) {
			// Move to next step
			nextStep();
		}
	}, [description, selectedDevices, video, currentStep]);

	const onFinish = useCallback(async () => {
		// Handle final form submission here
		setIsUploadingVideo(true);
		console.log("Final form data:", formData);

		try {
			if (loggedUserValue && description && video) {
				const videoUrl = await uploadProductVideo();

				if (videoUrl) {
					const newGuides: Guides_Insert_Input = {
						title: uuidv4(),
						description,
						status: GuideStatus.processing,
						video_url: videoUrl,
						created_by_user_id: loggedUserValue.id,
						workspace_id: loggedUserValue.workspace_id,
						organization_id: loggedUserValue.organization_id,
						// Link to handover when navigated from HandoverDetails
						...(handoverId ? { handover_id: handoverId } : {})
					};

					if (selectedDevices && selectedDevices.length) {
						const relatedProducts = selectedDevices.map(device => ({
							product_id: device.id,
							created_by_user_id: loggedUserValue.id
						}));

						newGuides.product_guides = {
							data: relatedProducts
						};
					}

					console.log("onFinish newGuides: ", newGuides);

					setIsUploadingVideo(false);
					addGuideMutation({
						variables: {
							input: newGuides
						}
					});
				}
			}
		} catch (error) {
			message.error("Failed to create Guide. Please try again.");
			console.error("Error in onFinish:", error);
		}
	}, [description, selectedDevices, video, loggedUserValue]);

	const CurrentStep = useMemo(() => {
		switch (currentStep) {
			case 0:
				return (
					<NewGuideDescriptionCard
						description={formData.description}
						onDescriptionChange={handleDescriptionChange}
						selectedDevices={formData.selectedDevices}
						onDeviceSelect={handleDeviceSelect}
						onNext={onNext}
						handoverId={handoverId}
					/>
				);
			case 1:
				return (
					<NewGuideAddVideo
						onRecord={handleVideoRecord}
						onNext={onFinish}
						isUploading={isUploadingVideo || isUploadingGuide}
						uploadProgress={videoUploadProgress}
					/>
				);
			default:
				return <></>;
		}
	}, [
		currentStep,
		formData,
		onFinish,
		onNext,
		handleDeviceSelect,
		handleDescriptionChange,
		handleVideoRecord,
		isUploadingVideo,
		isUploadingGuide,
		videoUploadProgress,
		handoverId
	]);

	return (
		<div className="products-details-screen-container">
			<Breadcrumb
				className="products-breadcrumbs"
				items={[
					{
						title: "Guides",
						onClick: () => navigate("/guides")
					},
					{
						title: "New Guide"
					}
				]}
			/>
			<Form form={form} layout="vertical" className="guide-details-container">
				{CurrentStep}
			</Form>
		</div>
	);
};

export default GuideAdd;
